import React from "react";
import {
  X,
  ChevronRight,
  Check,
  Star,
  Search,
  Building2,
  Globe,
  PlusCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { useWebsiteSelection } from "@/context/WebsiteSelectionContext";
import { Link as RouterLink } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

export function WebsiteSelectionModal() {
  const {
    isModalOpen,
    closeModal,
    selectedWebsite,
    // New context state and methods
    organisations,
    websites,
    isLoading,
    searchTerm,
    setSearchTerm,
    selectedOrganisation,
    handleWebsiteSelect,
    handleOrganisationSelect,
    // Computed values
    groupedWebsites,
    selectedOrganisationWebsites,
    filteredOrganisations,
    filteredSelectedOrganisationWebsites,
  } = useWebsiteSelection();

  return (
    <Dialog open={isModalOpen} onOpenChange={closeModal}>
      <DialogContent className="w-full max-w-4xl h-[80vh] flex flex-col">
        <DialogHeader className="p-6 border-b">
          <div className="w-full">
            <div className="flex justify-between items-center">
              <DialogTitle className="text-xl font-semibold">Select Website</DialogTitle>
            </div>
            <DialogDescription className="text-sm text-muted-foreground mt-1">
              Choose an organisation and website to work with
            </DialogDescription>
            {selectedWebsite && (
              <div className="mt-2 p-2 bg-primary/5 rounded border border-primary/20 w-full md:max-w-[50%]">
                <div className="text-xs text-muted-foreground">Currently selected:</div>
                <div className="font-medium text-sm">{selectedWebsite.name}</div>
                <div className="text-xs text-muted-foreground">
                  {selectedWebsite.organisation_name}
                </div>
              </div>
            )}
          </div>
        </DialogHeader>

        {/* Search Bar */}
        <div className="p-6 border-b">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search organisations and websites..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden">
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
                <p className="text-muted-foreground">
                  Loading organisations and websites...
                </p>
              </div>
            </div>
          ) : (
            <div className="flex h-full">
              {/* Left Column - Organisations */}
              <div className="w-1/2 border-r p-6 overflow-y-auto">
                <h3 className="font-medium text-sm text-muted-foreground mb-4 uppercase tracking-wide">
                  Organizations
                </h3>
                <div className="space-y-2">
                  {filteredOrganisations.map((org) => {
                    const orgWebsites = groupedWebsites[org.id] || [];
                    const isSelected = selectedOrganisation === org.id;

                    return (
                      <div key={org.id}>
                        <div
                          className={`flex items-center justify-between p-3 rounded-lg cursor-pointer transition-colors ${
                            isSelected
                              ? "bg-primary/10 border border-primary/20"
                              : "hover:bg-muted/50"
                          }`}
                          onClick={() => handleOrganisationSelect(org.id)}
                        >
                          <div className="flex items-center gap-3">
                            <Building2 className="h-4 w-4 text-muted-foreground" />
                            <div>
                              <div className="font-medium">{org.name}</div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className="text-xs">
                              {orgWebsites.length} website
                              {orgWebsites.length !== 1 ? "s" : ""}
                            </Badge>
                            {org.current_plan && (
                              <Badge variant="secondary" className="text-xs">
                                {org.current_plan}
                              </Badge>
                            )}
                            <ChevronRight className="h-4 w-4 text-muted-foreground" />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Right Column - Websites for Selected Organisation */}
              <div className="w-1/2 p-6 overflow-y-auto">
                <h3 className="font-medium text-sm text-muted-foreground mb-4 uppercase tracking-wide">
                  Websites
                </h3>
                {selectedOrganisation ? (
                  <div className="space-y-2">
                    {filteredSelectedOrganisationWebsites.map((website) => (
                      <div
                        key={website.id}
                        className={`flex items-center justify-between p-3 rounded-lg cursor-pointer transition-colors ${
                          selectedWebsite?.id === website.id
                            ? "bg-primary/10 border border-primary/20"
                            : "hover:bg-muted/50"
                        }`}
                        onClick={() => handleWebsiteSelect(website)}
                      >
                        <div className="flex items-center gap-3">
                          <Globe className="h-4 w-4 text-muted-foreground" />
                          <div>
                            <div className="font-medium">{website.name}</div>
                            <div className="text-xs text-muted-foreground truncate max-w-[200px]">
                              {website.url}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {selectedWebsite?.id === website.id && (
                            <Check className="h-4 w-4 text-primary" />
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <Building2 className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p>Select an organisation to view websites</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t bg-muted/30">
          <div className="flex items-center justify-between">
            <div className="text-sm text-muted-foreground">
              {selectedOrganisation
                ? `${selectedOrganisationWebsites.length} website${
                    selectedOrganisationWebsites.length !== 1 ? "s" : ""
                  } in selected organisation`
                : `${websites.length} total website${
                    websites.length !== 1 ? "s" : ""
                  } across ${organisations.length} organisation${
                    organisations.length !== 1 ? "s" : ""
                  }`}
            </div>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" asChild>
                <RouterLink to="/websites">
                  <PlusCircle className="h-4 w-4 mr-2" />
                  Manage Websites
                </RouterLink>
              </Button>
              <Button variant="outline" onClick={closeModal}>
                Cancel
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
